import { gql } from '@apollo/client'

export const GET_DASHBOARD_COUNT = gql`
  query EmbarkationAdminDashboard($filter: EmbarkationAdminDashboardFilter!) {
    embarkationAdminDashboard(filter: $filter) {
      dashboard {
        guestEmbarkationStatistics {
          ...GuestStats
          guestEmbarkationStatisticsInPercentages {
            ...GuestStatsPercent
          }
        }
        crewMemberEmbarkationStatistics {
          ...CrewStats
        }
        visitorEmbarkationStatistics {
          ...VisitorStats
        }
        alertStatistics {
          active
          created
        }
        messageStatistics {
          unread
          created
        }
        message
      }
    }
  }

  fragment GuestStats on GuestEmbarkationStatistics {
    expected
    checkedIn
    onboard
    ashore
    notCheckedIn
    debarking
    totalOccupancy
    citizenshipGuestCount
    nonCitizenshipGuestCount
    embarkingToday
    ociDoneAndApproved
    ociDoneAndPending
    ociNotDone
    guestsWithAlerts
    checkedInOnboard
    checkedInAshore
    yetToEmbark
    yetToLeave
    specialNeedGuestCount
    notCheckedInAshore
    notCheckedInOnboard
    leftToday
    embarkedToday
    checkedOut
    notCheckedInEmbarkingToday
    notCheckedOutDebarkingToday
    guestsPassedToACI
    guestsPassedToValidate
  }

  fragment GuestStatsPercent on GuestEmbarkationStatisticsInPercentages {
    checkedInOnboard
    checkedInAshore
    ociDoneAndApproved
    ociDoneAndPending
    ociNotDone
    notCheckedInOnboard
  }

  fragment CrewStats on CrewMemberEmbarkationStatistics {
    expected
    checkedIn
    onboard
    ashore
    notCheckedIn
    debarking
    totalOccupancy
    embarkedToday
    leftToday
    leavingTodayCount
    embarkingToday
    yetToLeave
    notCheckedInEmbarkingToday
    notCheckedOutDebarkingToday
  }

  fragment VisitorStats on VisitorEmbarkationStatistics {
    expected
    onboard
    ashore
    approvalPending
    approved
    rejected
    checkedOut
    permanent
  }
`

export const GET_MASTER_DATA_QUERY = gql`
  query MasterData(
    $filter: CrewDepartmentFilter!
    $pageFilter: PageFilter!
    $shipCode: String!
    $portsPageFilter2: PageFilter!
    $shipByCodeShipCode2: String!
    $shipTimeByCodeShipCode2: String!
    $searchReferenceDataFilter2: ReferenceDataFilter!
    $searchReferenceDataPageFilter2: PageFilter!
    $pageSort: PageSort
    $voyagesWithPaginationFilter2: VoyageFilter!
    $voyagesWithPaginationPageFilter2: PageFilter
    $gangwayAlertCodesPageFilter2: PageFilter
    $statePageFilter: PageFilter
    $visaTypePageFilter: PageFilter
    $onlyCheckedIn: Boolean!
  ) {
    crewDepartments(filter: $filter) {
      id
      isDeleted
      name
      propertyId
      code
    }
    personAlertTypes {
      id
      code
      isDeleted
      name
      severity
    }
    countries(pageFilter: $pageFilter) {
      countries {
        id
        name
        resourceKey
        code
        isDeleted
        telephoneCode
        threeLetterCode
        twoLetterCode
      }
    }
    airlines(pageFilter: $pageFilter) {
      airlines {
        id
        name
        resourceKey
        code
        isDeleted
      }
    }
    airports(pageFilter: $pageFilter) {
      airports {
        id
        code
        name
        resourceKey
        countryCode
        isDeleted
      }
    }
    countryStates(pageFilter: $statePageFilter) {
      states {
        id
        name
        resourceKey
        code
        countryCode
        isDeleted
      }
    }
    visaTypes(pageFilter: $visaTypePageFilter) {
      visaTypes {
        id
        code
        name
        countryCode
        isDeleted
      }
    }
    documentTypes {
      id
      isDeleted
      name
      code
    }
    gangwayLocationsByShipCode(shipCode: $shipCode) {
      id
      isDeleted
      shipCode
      name
    }
    genders {
      id
      code
      isDeleted
      name
    }
    loyaltyLevelTypes {
      id
      isDeleted
      name
      code
      noOfCruiseNights
      brandCode
      sequence
    }
    personTypes {
      id
      isDeleted
      name
      code
    }
    phoneTypes {
      id
      name
      isDeleted
      isDefault
    }
    ports(pageFilter: $portsPageFilter2) {
      ports {
        id
        name
        code
      }
    }
    reservationStatuses {
      code
      name
    }
    shipByCode(shipCode: $shipByCodeShipCode2) {
      code
      brandCode
      brandName
      name
      mediaItemId
    }
    shipTimeByCode(shipCode: $shipTimeByCodeShipCode2) {
      utcOffset
      utcDate
      shipCode
    }
    specialNeedTypes {
      id
      categoryCode
      code
      name
    }
    preferences(pageFilter: $pageFilter) {
      preferences {
        values {
          code
          nameKey
          isDeleted
        }
      }
    }
    celebrations(pageFilter: $pageFilter) {
      celebrations {
        code
        name
        isDeleted
      }
    }
    visitPurposes {
      id
      isDeleted
      name
    }
    visitorDepartments {
      id
      name
    }
    visitorRejectionReasonCatagories {
      id
      code
      isDeleted
      name
      rejectionReasons {
        id
        isDeleted
        name
      }
    }
    visitorTypes {
      id
      code
      name
    }
    healthQuestions {
      title
      sequence
      isDeleted
      question
    }
    searchReferenceData(
      filter: $searchReferenceDataFilter2
      pageFilter: $searchReferenceDataPageFilter2
      pageSort: $pageSort
    ) {
      referenceData {
        id
        code
        name
        resourceKey
        type
      }
    }
    voyagesWithPagination(
      filter: $voyagesWithPaginationFilter2
      pageFilter: $voyagesWithPaginationPageFilter2
    ) {
      voyages {
        debarkDate
        # debarkPortCode
        embarkDate
        # embarkPortCode
        isActive
        isDeleted
        name
        shipCode
        number
        id
        itineraries {
          arrivalTime
          departureTime
          isSeaDay
          itineraryDay
          portCode
          id
        }
      }
    }
    gangwayAlertCodes(pageFilter: $gangwayAlertCodesPageFilter2) {
      gangwayAlertCodes {
        id
        alertTypeCode
        code
        departmentCode
        description
        name
      }
    }
    applicationReviewerUsers {
      id
      crewMember(onlyCheckedIn: $onlyCheckedIn) {
        ...CrewMemberFields
      }
    }
    applicationSubmitterUsers {
      id
      crewMember(onlyCheckedIn: $onlyCheckedIn) {
        ...CrewMemberFields
      }
    }
  }
  fragment CrewMemberFields on CrewMember {
    id
    designation
    departmentCode
    contactInfo {
      email
    }
    personalInfo {
      firstName
      lastName
    }
  }
`
export const GET_SHIPS_QUERY = gql`
  query Ships {
    ships {
      brandCode
      name
      mediaItemId
      code
      isOperational
    }
  }
`
